// @optimyl/config — runtime configuration loaded from /config.json at app startup.
//
// config.json lives in each app's public/ and is NOT baked into the JS bundle, so one built artifact can
// be promoted across environments (the release pipeline's File Transform step swaps values per env).
//
// THIS PACKAGE OWNS THE MECHANICS; EACH APP OWNS ITS SHAPE.
//
// Config fields differ per app — the employer app cannot start without ASSETS_BASE_URL and the CRM app has
// no use for it at all. So the shape is a type parameter, and each app declares its own interface extending
// CoreConfig plus the required-field list it cares about. An earlier revision declared every app's fields
// in one interface and marked the app-specific ones optional; that promised values which were `undefined`
// at runtime, and pushed one app's fields into another app's autocomplete.
//
// LOCAL DEV OVERRIDES:
// If /public/config.local.json exists (gitignored), its values are merged over /public/config.json. Use it
// to point your machine at a real environment without editing — and accidentally committing — config.json.
// It never ships: the release pipeline clones from git, and the app builds strip it from dist/.

/**
 * The fields every Optimyl web app's config.json carries. Apps extend this; the loader itself only reads
 * IDENTITY_URL, for the deployed-but-pointing-at-localhost check.
 *
 * AUTH_ENABLED is optional because only the employer app has an auth-disabled local-dev mode. The CRM app
 * always authenticates and does not carry the key.
 */
export interface CoreConfig {
  IDENTITY_URL: string;
  AUTH_ENABLED?: boolean;
  API_BASE_URL: string;
  APPINSIGHTS_CONNECTION_STRING: string;
  SF_ENVIRONMENT: string;
}

export interface LoadConfigOptions<T extends CoreConfig = CoreConfig> {
  /**
   * Fields this app cannot start without. Nothing is required unconditionally — a field one app depends on
   * is unused by another, and a package-level requirement would block an app on a value it never reads.
   */
  require?: (keyof T)[];
}

let _config: CoreConfig | null = null;

/**
 * Whether the app is being served from a developer's machine rather than a deployed host: localhost, the
 * RFC 1918 private ranges (a phone on the LAN hitting http://192.168.1.104:5173), and bs-local.com, which is
 * BrowserStack Local's alias for the developer's loopback and the only hostname its iOS devices can use.
 * Both the config.local.json override and the deployed-config consistency check key off this.
 */
export function isLocalDevHost(hostname: string): boolean {
  return /^(localhost|127\.|192\.168\.|10\.|172\.(1[6-9]|2\d|3[01])\.|bs-local\.com$)/.test(hostname);
}

export async function loadConfig<T extends CoreConfig>(options: LoadConfigOptions<T> = {}): Promise<T> {
  if (_config) return _config as T;

  const res = await fetch("/config.json");
  if (!res.ok) {
    throw new Error(`Failed to load /config.json: ${res.status}`);
  }
  const base = (await res.json()) as T;

  // Skip the override fetch when not on a local host — the file never ships to deployed envs and would
  // just produce a console 404 on every page load. The content-type check catches the SPA fallback, which
  // answers a missing path with index.html and a 200.
  if (isLocalDevHost(window.location.hostname)) {
    try {
      const localRes = await fetch("/config.local.json");
      if (localRes.ok && localRes.headers.get("content-type")?.includes("application/json")) {
        const overrides = (await localRes.json()) as Partial<T>;
        const merged = { ...base, ...overrides };
        validateConfig(merged, options);
        _config = merged;
        return merged;
      }
    } catch {
      /* no override present — fall through to the committed config */
    }
  }

  validateConfig(base, options);
  _config = base;
  return base;
}

function validateConfig<T extends CoreConfig>(config: T, options: LoadConfigOptions<T>): void {
  const errors: string[] = [];

  for (const key of options.require ?? []) {
    const value = config[key];
    if (value === undefined || value === null || value === "") errors.push(`${String(key)} is required`);
  }

  // A consistency check rather than a required-field one: a deployed app pointing at a developer's machine
  // is always a mistake, and the symptom (sign-in that never returns) does not suggest the cause.
  //
  // Not gated on AUTH_ENABLED. The hostname clause already means this only fires when DEPLOYED, and a
  // deployed app pointing at localhost is wrong whether or not auth is on — the flag was only suppressing
  // the check for apps that do not carry it. "Deployed" is the same test the override fetch uses: a LAN
  // address or BrowserStack's bs-local.com is a developer's machine too, and the committed localhost
  // IDENTITY_URL is correct there.
  if (config.IDENTITY_URL?.includes("localhost") && !isLocalDevHost(window.location.hostname)) {
    errors.push(`IDENTITY_URL points to localhost (${config.IDENTITY_URL}) but the app is deployed`);
  }

  if (errors.length > 0) {
    throw new Error(
      `Invalid config.json:\n- ${errors.join("\n- ")}\n\n` +
        `Running locally? An app may ship config.json without environment values on purpose. Create ` +
        `public/config.local.json (gitignored) with the fields above; it is merged over config.json on ` +
        `local hosts only.`,
    );
  }
}

export function getConfig<T extends CoreConfig>(): T {
  if (!_config) {
    throw new Error("Config not loaded. Call loadConfig() first.");
  }
  return _config as T;
}
